// Electorate report shell: pick an electorate AND a focal party, preview the A4 body,
// export to PDF. Single electorate, one button — no queue, no demo gating, no localStorage.
// Focal party (National / NZF / ACT only) drives element #8 (focal × Lab/Green/TPM) and
// any other "[PARTY]" element; Labour/Green/TPM are the fixed comparison set, not focal.
import React, { useState } from "react";
import {
  Box, Stack, Autocomplete, TextField, Button, CircularProgress, Typography,
  ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { CONSTITUENCIES } from "./constants/areas";
import { PARTIES } from "./constants/partyConfig";
import { useAccess } from "./access/AccessContext";
import ConstituencyReportView from "./ConstituencyReportView";
import { generatePdfFor } from "./utils/exportPdf";

// Only National / NZF / ACT have a report build, so only they can be focal.
const REPORTABLE = ["nat", "nzf", "act"];
const FOCAL_OPTIONS = REPORTABLE.map((k) => ({ key: k, ...PARTIES[k] }));

export default function ReportPage(sharedProps) {
  const { access } = useAccess();

  // A party worker only ever gets their own report, so there is nothing to choose
  // and the toggle is dropped entirely. Internal users keep the full picker.
  const focalOptions = access?.is_internal
    ? FOCAL_OPTIONS
    : FOCAL_OPTIONS.filter((p) => access?.visible_parties?.includes(p.key));
  const lockedFocal =
    !access?.is_internal && access?.lock_focal && REPORTABLE.includes(access?.focal_party)
      ? access.focal_party
      : null;

  const [area, setArea] = useState(CONSTITUENCIES[0] || null);
  const [chosenFocal, setFocal] = useState(focalOptions[0]?.key || "nat");
  const [generating, setGenerating] = useState(false);

  // The pinned party always wins over local state.
  const focal = lockedFocal || chosenFocal;
  const showFocalPicker = !lockedFocal && focalOptions.length > 1;
  // Only National / NZF / ACT have a report build. A party worker outside that set
  // gets nothing rather than silently falling back to another party's report.
  const noReportForParty = !access?.is_internal && !focalOptions.length;

  const handleExport = async () => {
    if (!area) return;
    setGenerating(true);
    try {
      const focalName = PARTIES[focal]?.shortLabel || focal;
      const safeName = String(area.area_name).replaceAll(" ", "_");
      // Ordered page list — one full A4 page per node, captured in order.
      await generatePdfFor(
        [`report-content-${area.area_id}-p1`, `report-content-${area.area_id}-p2`],
        `ElectorateProfile_${focalName}_${safeName}.pdf`
      );
    } catch (e) {
      console.error("[ReportPage] export failed:", e);
    } finally {
      setGenerating(false);
    }
  };

  if (noReportForParty) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100%", bgcolor: "#f7f8fa", px: 3 }}>
        <Typography color="text.secondary" align="center">
          No electorate report is available for your party.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#f7f8fa" }}>
      {/* Control bar — single row, all controls on a shared centreline */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 3, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper", flexWrap: "wrap", rowGap: 1 }}
      >
        <Autocomplete
          options={CONSTITUENCIES}
          value={area}
          onChange={(_, v) => setArea(v)}
          getOptionLabel={(o) => o?.area_name || ""}
          isOptionEqualToValue={(o, v) => o?.area_id === v?.area_id}
          sx={{
            width: 280,
            "& .MuiAutocomplete-endAdornment": { right: 8, border: "none" },
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": { pr: "56px" },
            // single clean field outline — no bordered/divider sub-box around the chevron
            "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
              border: "none", borderLeft: "none", borderRadius: 0, background: "transparent", boxShadow: "none",
            },
          }}
          renderInput={(params) => <TextField {...params} size="small" label="Electorate" placeholder="Search…" />}
        />

        {showFocalPicker ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>Party</Typography>
            <ToggleButtonGroup
              value={focal}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) setFocal(v); }}
            >
              {focalOptions.map((p) => (
                <ToggleButton key={p.key} value={p.key} sx={{ px: 1.5, textTransform: "none" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color, mr: 0.75 }} />
                  {p.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        ) : null}

        <Button
          variant="contained"
          onClick={handleExport}
          disabled={!area || generating}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          sx={{ bgcolor: "#111827", "&:hover": { bgcolor: "#0f172a" }, ml: "auto" }}
        >
          {generating ? "Exporting…" : "Export PDF"}
        </Button>
      </Stack>

      {/* Preview */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3, display: "grid", placeItems: "start center" }}>
        {area ? (
          // The report view now renders both pages as its own stacked A4 Papers.
          <ConstituencyReportView area={area} focal={focal} {...sharedProps} />
        ) : (
          <Typography color="text.secondary">Select an electorate to preview its report.</Typography>
        )}
      </Box>
    </Box>
  );
}
